import React, { FC, useContext, useEffect, useState } from 'react';
import {
  Alert,
  css,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import { Box } from '@mui/material';
import { TextField, Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { usePostWotcSurvey } from '@features/wotc/hooks/usePostWotcSurvey';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { WotcSurveyData } from './types';
import { authContext } from '@staffedupusa/shared-components';
import dayjs, { Dayjs } from 'dayjs';
import { VerificationIcon } from '../../vectors/index';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useCreateApplicationNote } from '@features/notifications/hooks/useReadNotification';
import { useUpdateStatus } from '@features/profile/hooks/useUpdateStatus';

type WotcVerificationModalProps = {
  generalInfoModal: boolean;
  notificationId?: number;
  wotcSurveyData: WotcSurveyData;
  handleClose: () => void;
  handleNext: () => void;
  handleBack: () => void;
};
interface FormData {
  verification1: boolean;
  verification2: boolean;
  verification3: string;
  verification4: string;
  verification5: string | null | Dayjs;
  currentDate: string;
}

const defaultValues = {
  verification1: false,
  verification2: false,
  verification3: '',
  verification4: '',
  verification5: null,
  currentDate: dayjs().format('YYYY-MM-DD'),
};

const WotcVerificationModal: FC<WotcVerificationModalProps> = ({
  generalInfoModal,
  handleClose,
  wotcSurveyData,
  notificationId,
  handleNext,
  handleBack,
}) => {
  const [errorText, setErrorText] = useState('');
  const { sendWotcSurvey, isSendingWotcSurvey } = usePostWotcSurvey({
    setErrorText,
  });
  const { readNotification, isReadNotificationLoading } =
    useCreateApplicationNote({ setErrorText });
  const { updateStatus, isUpdatingStatus } = useUpdateStatus({
    setAlertText: setErrorText,
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>({
    defaultValues,
  });
  // Function to handle form submission
  const onSubmit = (data: FormData) => {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        toast.success('Data submitted successfully');
        handleNext();
        handleClose();
        updateStatus({ walkthrough_status: 1, wotc_completed: true }, options);
        if (notificationId) {
          readNotification({
            notification_id: `${notificationId}`,
          });
        }
      },
    };

    const transformedData = {
      ...data,
      verification1: data.verification1 ? 'Y' : 'N',
      verification2: data.verification2 ? 'Y' : 'N',
      verification3: data.verification3,
      verification4: data.verification4,
      verification5: data.verification5
        ? data.verification5.toISOString().split('T')[0]
        : null,
      verification_timestamp: data.currentDate,
      completed: data.currentDate,
      status: 10,
    };
    sendWotcSurvey({ ...transformedData }, options);
  };

  useEffect(() => {
    if (wotcSurveyData) {
      Object.keys(defaultValues).forEach((key) => {
        const typedKey = key as keyof typeof defaultValues &
          keyof WotcSurveyData;
        if (wotcSurveyData[typedKey] !== undefined) {
          if (typedKey === 'verification5') {
            if (wotcSurveyData[typedKey] !== null) {
              const dateValue = wotcSurveyData[typedKey];
              const date = dateValue ? dayjs(dateValue) : null;
              setValue(typedKey, date);
            } else {
              const dateValue = wotcSurveyData['bdi_dob'];
              const date = dateValue ? dayjs(dateValue) : null;
              setValue(typedKey, date);
            }
          } else if (typedKey === 'verification3') {
            if (wotcSurveyData[typedKey] !== null) {
              setValue(typedKey, wotcSurveyData[typedKey]);
            } else {
              setValue(
                typedKey,
                `${wotcSurveyData['bdi_first_name']} ${wotcSurveyData['bdi_last_name']}`,
              );
            }
          } else if (wotcSurveyData[typedKey] !== null) {
            setValue(typedKey, wotcSurveyData[typedKey]);
          }
        }
      });
    }
  }, [wotcSurveyData, setValue]);

  return (
    <Dialog
      open={generalInfoModal}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', right: 8, top: 8 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap="10px"
        >
          <VerificationIcon />
          <Typography variant="h5">Verification</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mt={3}>
          {errorText && (
            <Alert sx={{ mb: 3 }} severity="error">
              {errorText}
            </Alert>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={4}>
              <Grid item>
                {/* Checkbox 1 */}
                <Controller
                  name="verification1"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="I authorize the Department of Veterans affairs, Rehabilitation and correction, Vocational Rehabilitation, or social security Administration for SSI to provide the verification or information requested by StaffedUp solutions or the State Workforce agencies and release the information to those entities as requested."
                    />
                  )}
                />
                {errors.verification1 && (
                  <span
                    style={{ color: 'red', margin: '3px', fontSize: '12px' }}
                  >
                    This field is required.
                  </span>
                )}
              </Grid>

              <Grid item>
                {/* Checkbox 2 */}
                <Controller
                  name="verification2"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Under penalties of perjury, I declare that I gave the above information to the employer on or before the day I was offered a job, and it is, to the best of my knowledge, true, correct, and complete."
                    />
                  )}
                />
                {errors.verification2 && (
                  <span
                    style={{ color: 'red', margin: '3px', fontSize: '12px' }}
                  >
                    This field is required.
                  </span>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                {/* Full Name */}
                <Controller
                  name="verification3"
                  control={control}
                  rules={{ required: 'Full Name is required.' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Full Name"
                      size="small"
                      fullWidth
                      error={!!errors.verification3}
                      helperText={errors.verification3?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {/* Social Security Number */}
                <Controller
                  name="verification4"
                  control={control}
                  rules={{
                    required: 'Social Security Number is required.',
                    pattern: {
                      value: /^\d{9}$/,
                      message:
                        'Social Security Number must be exactly 9 digits.',
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Social Security Number"
                      fullWidth
                      size="small"
                      type="text"
                      inputMode="numeric"
                      inputProps={{ maxLength: 9 }}
                      onChange={(e) => {
                        const onlyNums = e.target.value.replace(/\D/g, '');
                        field.onChange(onlyNums);
                      }}
                      error={!!errors.verification4}
                      helperText={errors.verification4?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {/* Date of Birth */}
                <Controller
                  name="verification5"
                  control={control}
                  rules={{ required: 'Birth Date is required' }}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label="Birth Date"
                      sx={{
                        width: '100%',
                        '.MuiInputLabel-root': {
                          top: '-6px',
                          fontSize: '0.875rem',
                        },
                        '.MuiInputBase-root': {
                          height: 40,
                        },
                        '.MuiInputBase-input': {
                          padding: '10px 12px',
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                  )}
                />
                {errors.verification5 && (
                  <span
                    style={{ color: 'red', margin: '3px', fontSize: '12px' }}
                  >
                    Birth Date is required
                  </span>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                {/* Current Date (disabled, automatically set to current date) */}
                <Controller
                  name="currentDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Current Date"
                      type="date"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      disabled
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Box width="100%" textAlign="center">
              <LoadingButton
                loading={isSendingWotcSurvey}
                sx={{ width: '100%', mt: 3 }}
                type="submit"
                variant="contained"
                color="primary"
              >
                Next
              </LoadingButton>
            </Box>
          </form>
          <Box width="100%" textAlign="center">
            <LoadingButton
              sx={{ width: '100%', mt: 3 }}
              variant="outlined"
              color="primary"
              onClick={handleBack}
              disabled={isSendingWotcSurvey || isUpdatingStatus}
            >
              Back
            </LoadingButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WotcVerificationModal;
